import {SessionLogs} from '../session-logs/SessionLogs'
import {EventSessionLogs} from '../session-logs/EventSessionLogs'
import {SessionHeader} from './SessionHeader'
import {SessionFooter, SessionMetadataBar} from './SessionFooter'
import {ArtifactList} from './ArtifactList'
import {SessionFailedBanner} from '../session-logs/SessionFailedBanner'
import {SessionCancelledBanner} from '../session-logs/SessionCancelledBanner'
import {SessionTimedOutBanner} from '../session-logs/SessionTimedOutBanner'
import {AnimatedCollapse} from '../AnimatedCollapse'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {HighlightedUserText} from '@github-ui/copilot-code-prettify/render/HighlightedUserText'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {ChannelSessionHistoryTimeline} from '../session-logs/ahp/ChannelSessionHistoryTimeline'
import {AhpSessionTimeline} from '../session-logs/ahp/AhpSessionTimeline'

import {useEffect, useRef, useState} from 'react'
import {useSessionContext} from '../../contexts/SessionContext'
import type {Pull} from '../../types/pull'
import {isActiveSession, SessionState} from '../../types/session'
import {canSteerMessagingAppDm, isChannelSession} from '../../utils/channel-session'
import {useCurrentUserOptional} from '../../contexts/CurrentUserContext'
import {useAgentProfile} from '../../contexts/agent-profile-context'
import {SessionLogsProvider} from '../session-logs/Context'
import {useSelectedModel} from '../../hooks/use-selected-model'
import {generateWorkflowRunLink} from '../../utils/generate-actions-link'
import styles from './Session.module.css'
import {clsx} from 'clsx'
import {useIsAhpSession} from '../../hooks/use-is-ahp-session'
import type {ContributedInstructionsMetadata, EstablishedAhpSession} from '@github-ui/ahp-session-client/contracts'
import type {APITask} from '../../types/api-task'
import type {APITaskArtifact} from '../../types/artifact'
import type {TimelineMarker} from '../session-logs/ahp/AhpTimelineView'
import {shouldUseEmbeddedSandboxPresentation} from '../../utils/repoless-chat-task'

const GLOBAL_TOGGLE_EVENT = 'session-global-toggle'

function renderUserMessageContent(text: string, {collapsible}: {collapsible: boolean}) {
  return text.includes('```') ? <HighlightedUserText text={text} collapsible={collapsible} /> : null
}

interface SessionProps {
  defaultOpen?: boolean
  onOpenChange: (open: boolean) => void
  onToolExpand?: (expand: boolean) => void
  shouldHighlight?: boolean
  pull?: Pull
  logsPollingInterval?: number
  nwo?: string
  establishedAhpSession?: EstablishedAhpSession
  establishedAhpSessionRequired?: boolean
  taskAliveChannel?: string
  taskCreatorType?: APITask['creator_type']
  timelineMarkers?: TimelineMarker[]
  onContributedInstructions?: (instructions: ContributedInstructionsMetadata, trigger: HTMLElement) => void
  preserveAhpHistory?: boolean
  isRepolessChatTask?: boolean
  queueReadOnly?: boolean
  loadingPresentation?: 'local' | 'page'
  suppressLocalLifecycleLoading?: boolean
  artifacts?: APITaskArtifact[]
}

export function Session({
  defaultOpen = false,
  onOpenChange,
  onToolExpand,
  shouldHighlight = false,
  pull,
  logsPollingInterval,
  nwo,
  establishedAhpSession,
  establishedAhpSessionRequired = false,
  taskAliveChannel,
  taskCreatorType,
  timelineMarkers,
  onContributedInstructions,
  preserveAhpHistory,
  isRepolessChatTask = false,
  queueReadOnly,
  loadingPresentation = 'local',
  suppressLocalLifecycleLoading,
  artifacts,
}: SessionProps) {
  const {session} = useSessionContext()
  const selectedModel = useSelectedModel(session.model)
  const {isAhpSession, reachable, wakeable, environment, loading: ahpEligibilityPending} = useIsAhpSession(session)
  // Managed CCA sessions render the model-facing kickoff seed as their first
  // AHP turn. The legacy non-AHP log view dropped that sourceless kickoff (it
  // matched no steering command and the session is non-interactive) and showed
  // the friendly "started a task" message instead, so suppress the equivalent
  // first user bubble here to keep parity.
  const managedCca = environment?.kind === 'managed-cca'
  const embeddedSandboxPresentation = shouldUseEmbeddedSandboxPresentation({
    isRepolessChatTask,
    isAhpSession,
    ahpEligibilityPending,
    environmentKind: environment?.kind,
  })

  // Use provided nwo, fall back to pull's repository_nwo for backwards compatibility
  const effectiveNwo = nwo ?? pull?.repository_nwo

  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [logsAreLoading, setLogsAreLoading] = useState(false)
  const sessionContainerRef = useRef<HTMLDivElement>(null)
  const toolInstanceRef = useRef({})

  const {singleSessionAgent: agentSingleSession, interactiveSession: useEventsSchema} = useAgentProfile()
  const channelSession = isChannelSession(session)
  const viewerOwnsSession = useCurrentUserOptional()?.currentUserIsSessionOwner ?? false
  const steerableDm = canSteerMessagingAppDm({
    session,
    creatorType: taskCreatorType,
    viewerOwnsSession,
  })

  // AHP-backed sessions render like CLI sessions: one session per task,
  // header hidden, content always expanded. The collapsible per-session
  // header isn't useful when there will only ever be one.
  const singleSessionAgent = agentSingleSession || isAhpSession || channelSession

  const handlerRef = useRef<(event: Event) => void>((event: Event) => {
    const customEvent = event as CustomEvent<{expand: boolean; excludeInstance: object}>
    if (customEvent.detail.excludeInstance !== toolInstanceRef.current) {
      const shouldExpand = customEvent.detail.expand
      setIsOpen(shouldExpand)
    }
  })

  // Set up global expand/collapse event listener
  useEffect(() => {
    const globalToggleHandler = (event: Event) => {
      handlerRef.current?.(event)
    }

    document.addEventListener(GLOBAL_TOGGLE_EVENT, globalToggleHandler)

    return () => {
      document.removeEventListener(GLOBAL_TOGGLE_EVENT, globalToggleHandler)
    }
  }, [])

  const handleModifierClick = (expand: boolean) => {
    setIsOpen(expand)

    // Dispatch event to all other tools
    const event = new CustomEvent(GLOBAL_TOGGLE_EVENT, {
      detail: {expand, excludeInstance: toolInstanceRef.current},
    })
    document.dispatchEvent(event)
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    onOpenChange(open)
  }

  const isActive = isActiveSession(session)
  const contentId = `session-content-${session.id}`
  const shouldShowSessionFooter =
    (session.state === SessionState.Completed || session.state === SessionState.InProgress) &&
    session.premium_requests > 0 &&
    isAhpSession &&
    !logsAreLoading
  const shouldShowSessionFooterFeedbackButtons = !isActive && (!isAhpSession || (!ahpEligibilityPending && !reachable))

  // Scroll to this session if it should be highlighted.
  useEffect(() => {
    if (!shouldHighlight) return
    if (!sessionContainerRef.current) return

    // Use back-to-back `requestAnimationFrame`s to wait for layout to settle before scrolling.
    let frameId = 0
    let secondFrameId = 0
    frameId = requestAnimationFrame(() => {
      secondFrameId = requestAnimationFrame(() => {
        sessionContainerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      })
    })

    return () => {
      cancelAnimationFrame(frameId)
      cancelAnimationFrame(secondFrameId)
    }
  }, [shouldHighlight])

  const containerClassNames = clsx(
    styles.contentContainer,
    isFeatureEnabled('agent_sessions_collapse_animation') && styles.animated,
  )

  const isExpanded = isOpen || singleSessionAgent

  const sessionContent = (
    <>
      <div className={styles.logsContainer}>
        <SessionLogsProvider
          variant="mission-control"
          onToolExpand={onToolExpand}
          session={session}
          sessionNwo={effectiveNwo}
          alignToolbarWithMessage={embeddedSandboxPresentation}
          renderUserMessageContent={
            copilotFeatureFlags.oneCopilotChat && isAhpSession ? renderUserMessageContent : null
          }
        >
          {channelSession && !steerableDm ? (
            <ChannelSessionHistoryTimeline
              session={session}
              taskAliveChannel={taskAliveChannel}
              creatorType={taskCreatorType}
              onContributedInstructions={onContributedInstructions}
              loadingPresentation={loadingPresentation}
            />
          ) : isAhpSession ? (
            <AhpSessionTimeline
              session={session}
              creatorType={taskCreatorType}
              preserveHistory={preserveAhpHistory || environment?.kind === 'managed-sandbox'}
              establishedAhpSession={establishedAhpSession}
              establishedAhpSessionRequired={establishedAhpSessionRequired}
              eligibilityPending={ahpEligibilityPending}
              wakeable={wakeable}
              suppressInitialUserMessage={managedCca}
              timelineMarkers={timelineMarkers}
              onContributedInstructions={onContributedInstructions}
              queueReadOnly={queueReadOnly}
              loadingPresentation={loadingPresentation}
            />
          ) : useEventsSchema ? (
            <EventSessionLogs
              taskId={session.task_id}
              pollingInterval={logsPollingInterval ?? 3000}
              loadingPresentation={loadingPresentation}
            />
          ) : (
            <SessionLogs
              logsPollingInterval={logsPollingInterval ?? 3000}
              nwo={effectiveNwo}
              onLogsLoadingChange={setLogsAreLoading}
              loadingPresentation={loadingPresentation}
              suppressLocalLifecycleLoading={suppressLocalLifecycleLoading}
            />
          )}
        </SessionLogsProvider>
      </div>
      <ArtifactList artifacts={artifacts} nwo={effectiveNwo} />
      {shouldShowSessionFooter && (
        <SessionFooter
          showFeedbackButtons={shouldShowSessionFooterFeedbackButtons}
          usage={{type: 'premium-requests', amount: session.premium_requests}}
          model={selectedModel?.displayName}
          reasoningEffort={session.reasoning_effort}
          variant={embeddedSandboxPresentation ? 'embedded' : 'legacy'}
        />
      )}
      <SessionMetadataBar placement="inside-session" loading={logsAreLoading} />
    </>
  )

  return (
    <div ref={sessionContainerRef} data-active={isActive} data-session-id={session.id}>
      {!singleSessionAgent && (
        <SessionHeader
          nwo={effectiveNwo}
          isCollapsed={!isOpen}
          onModifierClick={handleModifierClick}
          contentId={contentId}
          onOpenChange={handleOpenChange}
          shouldHighlight={shouldHighlight}
        />
      )}
      <div id={contentId} className={containerClassNames} data-expanded={isExpanded}>
        {!singleSessionAgent ? (
          <AnimatedCollapse open={isExpanded} keepMounted>
            {sessionContent}
          </AnimatedCollapse>
        ) : (
          sessionContent
        )}
      </div>
      <SessionErrorBanner nwo={effectiveNwo} />
    </div>
  )
}

export function SessionErrorBanner({nwo}: {nwo?: string}) {
  const {session} = useSessionContext()

  const isCancelled = session.state === SessionState.Cancelled
  const isTimedOut = session.state === SessionState.TimedOut

  // For non-cancelled states, only render when an explicit error message is available.
  // For cancelled sessions, always render so that users see feedback even when the backend
  // didn't attach a message (e.g. when the in-flight quota check stops the session).
  // For timed out sessions, always render to surface timeout context even if no explicit
  // message was attached by the backend.
  if (!isCancelled && !isTimedOut && !session.error?.message) return null

  const workflowLink = session.workflow_run_id
    ? (generateWorkflowRunLink(nwo ?? null, session.workflow_run_id) ?? null)
    : null

  if (isCancelled) {
    return (
      <div className="tmp-mt-3">
        <SessionCancelledBanner cancellationMessage={session.error?.message ?? null} workflowRunLink={workflowLink} />
      </div>
    )
  }

  if (isTimedOut) {
    return (
      <div className="tmp-mt-3">
        <SessionTimedOutBanner timeoutMessage={session.error?.message ?? null} workflowRunLink={workflowLink} />
      </div>
    )
  }

  // Type narrowing: the early `return null` above only short-circuits when
  // `!isCancelled && !isTimedOut && !session.error?.message`, so on the
  // failed/non-cancelled path
  // we know `session.error?.message` is truthy. TS can't prove that from the
  // optional chain, so the `?? ''` keeps the prop typed as `string`.
  return (
    <div className="tmp-mt-3">
      <SessionFailedBanner errorMessage={session.error?.message ?? ''} workflowRunLink={workflowLink} />
    </div>
  )
}
